import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { buttonName } from "../../../../../locales/en";
import IndustrySectorFilter from "../../../../../pages/reports/industry-sectors/IndustrySectorFilter";
import IndustryReportDownloadModal from "../../../../modals/IndustryReportDownloadModal";
import { useDispatch, useSelector } from "react-redux";
import {
  LENDING,
  MONTH,
  CURRENCY_FORMAT,
  STATUS12,
  CUSTOMERS,
  CUSTOMERTYPE,
  INVOICE
} from "../../../../../utils/constants/appConstants";
import { getIndustryReport } from "../../../../../redux/userReportsSlice";
import "./../../reportable/reportable.scss";
import TableSkeleton from "../../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../../hooks/useVisibleColumnCount";
import MonthlyKpiReportInvoiceModal from "../../../../modals/MonthlyKpiReportInvoiceModal";
import MonthlyKpiReportCustomerModal from "../../../../modals/MonthlyKpiReportCustomerModal";
import ErrorBoundary from "../../../../error-boundary";
import ListViewFallback from "../../../../error-boundary/fallback/ListViewFallback";
import RowItem from "./RowItem";
import { PERIOD_BY_VIEWTYPE } from "../../../../../utils/getDropdownOptions";

const IndustrySectorTable = () => {
  const dispatch = useDispatch();
  const invoiceIdsListRef = useRef(null);
  const { industrySectorLoader } = useSelector((state) => state.userReport);
  const { headerRef } = useVisibleColumnCount();
  const [tableData, setTableData] = useState({});
  const [addReportDownload, setAddReportDownload] = useState(false);
  const initialValues = {
    viewType: MONTH,
    period: STATUS12,
    countrySelect: "",
    dataType: LENDING
  };
  const [values, setValues] = useState(initialValues);
  const prevPeriodRef = useRef(values.period);
  const [activePeriod, setActivePeriod] = useState(values.period);
  const [addInvDtlsModal, setAddInvDtlsModal] = useState(false);
  const [addcustmrModal, setAddcustmrModal] = useState(false);

  const toggleAddInvDtlsModal = () => {
    setAddInvDtlsModal(!addInvDtlsModal);
  };
  const toggleAddcustmrModal = () => {
    setAddcustmrModal(!addcustmrModal);
  };

  const toggleReportDownload = () => {
    setAddReportDownload(!addReportDownload);
  };

  const HeaderCell = React.memo(({ header }) => <th>{header || ""}</th>);
  HeaderCell.displayName = "HeaderCell";
  HeaderCell.propTypes = { header: PropTypes.any };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "period") {
      setActivePeriod(prevPeriodRef.current);
      prevPeriodRef.current = value;
    }
    setValues((prev) => {
      if (name === "viewType") {
        return {
          ...prev,
          viewType: value,
          period: PERIOD_BY_VIEWTYPE[value] ?? ""
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleIndustryReportData = async () => {
    try {
      const paramsData = {
        viewType: values.viewType || MONTH,
        periods: values.period || STATUS12,
        country: values.countrySelect || undefined,
        dataType: values.dataType || LENDING
      };

      const response = await dispatch(getIndustryReport(paramsData)).unwrap();

      const responseData = response.data;
      if (response?.status === 200) {
        setTableData(responseData);
      }
    } catch {
      // handle catch here;
    }
  };
  useEffect(() => {
    handleIndustryReportData();
  }, [values.viewType, values.period, values.countrySelect, values.dataType]);

  const filterType = values.dataType === LENDING ? INVOICE : CUSTOMERTYPE;
  const handleOpenDataModal = (rowData) => {
    // Determine which modal to open
    const isLending = values.dataType === LENDING;
    const isCustomers = values.dataType === CUSTOMERS;
    if (isLending) setAddInvDtlsModal(true);
    if (isCustomers) setAddcustmrModal(true);
    invoiceIdsListRef.current = rowData?.ids || [];
  };

  const expectedColumnCount = useMemo(() => {
    const viewType = values.viewType?.toLowerCase();
    const period = parseInt(activePeriod) || 0;
    switch (viewType) {
      case "month":
        return period + 2;
      case "quarter":
        return period + 2;
      case "year":
        return 12;
      default:
        return 12;
    }
  }, [values.viewType, activePeriod]);

  const dollerSign = values.dataType === LENDING ? CURRENCY_FORMAT : "";

  const commonModalProps = {
    filterType,
    invoiceIdsListRef
  };

  return (
    <>
      <div className="col-md-12 d-flex flex-wrap mt-3 mb-3 justify-content-between search">
        <IndustrySectorFilter
          values={values}
          handleInputChange={handleInputChange}
          loading={industrySectorLoader}
        />
        <button onClick={toggleReportDownload} className="btn btn-primary">
          {buttonName?.download}
        </button>
      </div>
      <div className="col-md-12">
        <div className="table-responsive">
          <ErrorBoundary fallback={<ListViewFallback />}>
            <table
              className={`mt-2 mb-3 overduetable monthly-report-table bgless_table border-all`}
            >
              <thead ref={headerRef}>
                <tr>
                  {tableData?.data?.[0]?.map((header, index) => (
                    <HeaderCell header={header} key={index} />
                  ))}
                </tr>
              </thead>
              {industrySectorLoader ? (
                <tbody>
                  <TableSkeleton rows={8} columns={expectedColumnCount} />
                </tbody>
              ) : (
                <tbody>
                  {tableData?.data?.slice(1)?.map((row, rowIndex) => (
                    <RowItem
                      key={rowIndex}
                      row={row}
                      rowIndex={rowIndex}
                      onOpenModal={handleOpenDataModal}
                      dollerSign={dollerSign}
                      headerLength={tableData?.data?.[0]?.length || 1}
                    />
                  ))}
                </tbody>
              )}
            </table>
          </ErrorBoundary>
        </div>
      </div>

      <MonthlyKpiReportInvoiceModal
        isOpen={addInvDtlsModal}
        toggle={toggleAddInvDtlsModal}
        {...commonModalProps}
      />

      <MonthlyKpiReportCustomerModal
        isOpen={addcustmrModal}
        toggle={toggleAddcustmrModal}
        {...commonModalProps}
      />

      <IndustryReportDownloadModal
        isOpen={addReportDownload}
        toggle={toggleReportDownload}
      />
    </>
  );
};

export default IndustrySectorTable;
